import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { errorMessage, idempotencyKey } from "./format";

export const MAX_DICTATION_SECONDS = 120;
export const MAX_DICTATION_BYTES = 12 * 1024 * 1024;

const RECORDING_TYPES = ["audio/mp4", "audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/ogg"] as const;

export type DictationPhase = "idle" | "recording" | "uploading" | "transcribing";

export function baseAudioType(value: string): string {
  return value.split(";", 1)[0]?.trim().toLowerCase() ?? "";
}

export function recordingExtension(mediaType: string): string | null {
  switch (baseAudioType(mediaType)) {
    case "audio/mp4": return "m4a";
    case "audio/webm": return "webm";
    case "audio/ogg": return "ogg";
    case "audio/wav":
    case "audio/x-wav": return "wav";
    case "audio/mpeg": return "mp3";
    default: return null;
  }
}

export function selectRecordingType(supports: (mediaType: string) => boolean): string | undefined {
  return RECORDING_TYPES.find((mediaType) => supports(mediaType));
}

export function appendTranscript(draft: string, transcript: string): string {
  const clean = transcript.trim();
  if (clean.length === 0) return draft;
  return draft.trim().length === 0 ? clean : `${draft.trimEnd()} ${clean}`;
}

export function formatRecordingTime(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function microphoneCaptureSupported(): boolean {
  const mediaDevices = (navigator as unknown as { mediaDevices?: { getUserMedia?: unknown } }).mediaDevices;
  return typeof MediaRecorder !== "undefined" && typeof mediaDevices?.getUserMedia === "function";
}

type Options = {
  conversationId: string;
  disabled: boolean;
  onTranscript: (text: string) => void;
};

export function useVoiceDictation({ conversationId, disabled, onTranscript }: Options) {
  const createUploadUrl = useMutation(api.communication.createUploadUrl);
  const requestTranscription = useMutation(api.communication.requestSpeechTranscription);
  const cancelTranscription = useMutation(api.communication.cancelSpeechTranscription);
  const acknowledgeTranscription = useMutation(api.communication.acknowledgeSpeechTranscription);
  const [phase, setPhase] = useState<DictationPhase>("idle");
  const [seconds, setSeconds] = useState(0);
  const [jobId, setJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const jobRef = useRef<string | null>(null);
  const discardRecordingRef = useRef(false);
  const handledJobRef = useRef<string | null>(null);
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;
  const status = useQuery(api.communication.speechTranscriptionStatus, jobId === null ? "skip" : { jobId });

  const stopTimer = () => {
    if (timerRef.current !== null) window.clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const stopStream = () => {
    for (const track of streamRef.current?.getTracks() ?? []) track.stop();
    streamRef.current = null;
  };

  useEffect(() => {
    if (jobId === null || status === undefined || status === null || handledJobRef.current === jobId) return;
    if (status.state === "completed" && status.text !== undefined) {
      handledJobRef.current = jobId;
      onTranscriptRef.current(status.text);
      void acknowledgeTranscription({ jobId }).catch(() => undefined);
      jobRef.current = null;
      setJobId(null);
      setPhase("idle");
      setError(null);
    } else if (status.state === "failed" || status.state === "canceled") {
      handledJobRef.current = jobId;
      void acknowledgeTranscription({ jobId }).catch(() => undefined);
      jobRef.current = null;
      setJobId(null);
      setPhase("idle");
      setError("I couldn’t transcribe that recording. Please try again or type the message.");
    } else if (status.state === "transcribing") {
      setPhase("transcribing");
    }
  }, [acknowledgeTranscription, jobId, status]);

  useEffect(() => () => {
    discardRecordingRef.current = true;
    stopTimer();
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
    stopStream();
    if (jobRef.current !== null) void cancelTranscription({ jobId: jobRef.current }).catch(() => undefined);
  }, [cancelTranscription, conversationId]);

  const uploadAndRequest = async (blob: Blob, mediaType: string) => {
    const extension = recordingExtension(mediaType);
    if (extension === null || blob.size < 1 || blob.size > MAX_DICTATION_BYTES) throw new Error("That recording is empty, too large, or uses an unsupported format.");
    const filename = `yodl-dictation-${Date.now()}.${extension}`;
    setPhase("uploading");
    const uploadUrl = await createUploadUrl({ conversationId });
    const response = await fetch(uploadUrl, { method: "POST", headers: { "Content-Type": baseAudioType(mediaType) }, body: blob });
    if (!response.ok) throw new Error("The recording could not be uploaded securely.");
    const { storageId } = await response.json() as { storageId: string };
    const result = await requestTranscription({ conversationId, storageId, filename, mediaType: baseAudioType(mediaType), size: blob.size, idempotencyKey: `speech-${idempotencyKey()}` });
    if (!result.ok) throw new Error(result.error);
    const nextJobId = result.jobId;
    handledJobRef.current = null;
    jobRef.current = nextJobId;
    setJobId(nextJobId);
    setPhase("transcribing");
  };

  const start = async () => {
    if (disabled || phase !== "idle") return;
    setError(null);
    if (!microphoneCaptureSupported()) {
      setError("This browser does not support microphone dictation. Try Safari or Chrome.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      streamRef.current = stream;
      const selectedType = selectRecordingType((mediaType) => MediaRecorder.isTypeSupported(mediaType));
      const recorder = selectedType === undefined ? new MediaRecorder(stream) : new MediaRecorder(stream, { mimeType: selectedType });
      recorderRef.current = recorder;
      chunksRef.current = [];
      discardRecordingRef.current = false;
      recorder.ondataavailable = (event) => { if (event.data.size > 0) chunksRef.current.push(event.data); };
      recorder.onstop = () => {
        stopTimer();
        stopStream();
        const chunks = chunksRef.current;
        chunksRef.current = [];
        const mediaType = recorder.mimeType.length > 0 ? recorder.mimeType : (chunks[0]?.type ?? "");
        recorderRef.current = null;
        if (discardRecordingRef.current) return;
        void uploadAndRequest(new Blob(chunks, { type: mediaType }), mediaType).catch((caught: unknown) => {
          setPhase("idle");
          setError(errorMessage(caught));
        });
      };
      recorder.start(500);
      setSeconds(0);
      setPhase("recording");
      timerRef.current = window.setInterval(() => {
        setSeconds((current) => {
          const next = current + 1;
          if (next >= MAX_DICTATION_SECONDS && recorder.state === "recording") recorder.stop();
          return next;
        });
      }, 1000);
    } catch (caught) {
      stopStream();
      setPhase("idle");
      const message = errorMessage(caught);
      setError(/denied|permission|notallowed/i.test(message) ? "Allow microphone access in your browser, then tap the microphone again." : "The microphone could not start. Please try again.");
    }
  };

  const stop = () => {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  };

  return {
    phase,
    seconds,
    error,
    supported: microphoneCaptureSupported(),
    start,
    stop
  };
}
